<script lang="ts" context="module">
  export const navbarInfo = writable<{ rect: DOMRect } | null>(null);
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { afterNavigate } from '$app/navigation';

  import NavigationMenuMobile from './navigation-menu-mobile.svelte';
  import NavigationMenuDesktop from './navigation-menu-desktop.svelte';
  import NavigationHeaderDesktop from './navigation-header-desktop.svelte';
  import NavigationHeaderMobile from './navigation-header-mobile.svelte';
  import { writable } from 'svelte/store';

  import SearchPanel from './search-panel.svelte';

  import { cn } from '$lib/utils';
  import { bodyLock } from '$lib/actions/body-lock';
  import { escapeKey } from '$lib/actions/escape-key';
  import { buildPath } from '$lib/utils/paths';
  import { customSlide } from '$lib/transitions';
  import { reducedMotion } from '$lib/stores/reduced-motion';

  import { fade } from 'svelte/transition';
  import { user } from '$lib/stores/user';
  import { drawer } from '$lib/stores/drawer';

  import type { ConfigurationStoryblok } from '$types/bloks';
  import { layoutPanel } from '$lib/stores/layout-panel';
  import { trimId } from '$lib/utils/shopify/customerId';
  import { regionNudgeInfo } from '$components/region-nudge.svelte';
  import { region } from '$lib/region';

  import { createBreakpointMediaQueryStore } from '$lib/stores/media';
  import NavigationMenuBasicDesktop from './navigation-menu-basic-desktop.svelte';
  import NavigationMenuBasicMobile from './navigation-menu-basic-mobile.svelte';
  import { createDebouncedValue } from '$lib/stores/debounced';

  export let configuration: ConfigurationStoryblok;
  export let isBannerSticky: ConfigurationStoryblok['is_sticky'];
  export let abTestVersion: 'A' | 'B' | undefined;

  let ref: HTMLElement;
  let showProgress = false;

  const a11yIds = {
    triggerDesktop: 'navigation-menu-trigger',
    triggerSearch: 'search-menu-trigger',
    triggerMobile: 'navigation-mobile-menu-trigger',
    contentDesktop: 'navigation-menu',
    contentMobile: 'navigation-mobile-menu',
    contentSearch: 'search-menu'
  };

  $: headerContent = configuration.header_content;
  $: enableABTest = configuration.enable_navigation_abtest?.includes($region);

  $: hasHeaderContent =
    !!headerContent &&
    headerContent.length > 0 &&
    headerContent.some((item) => item.regions?.includes($region));

  $: isBasicNavbar = enableABTest ? abTestVersion === 'B' && hasHeaderContent : hasHeaderContent;

  const [rawNavInfo, debouncedNavInfo] = createDebouncedValue<{ rect: DOMRect } | null>(null, 200);

  debouncedNavInfo.subscribe((value) => {
    if (value && value.rect) {
      navbarInfo.set(value);
    }
  });

  afterNavigate(() => {
    layoutPanel.close();
  });

  $: if ($drawer.id) {
    layoutPanel.close();
  }

  onMount(() => {
    if (ref) {
      rawNavInfo.set({ rect: ref.getBoundingClientRect() });
    }
    showProgress = true;
  });

  $: if ($regionNudgeInfo.shouldDisplay) {
    updateNavInfo();
  }

  $: topnavBarHeight = `${($navbarInfo?.rect?.top || 0) + ($navbarInfo?.rect?.height || 0)}px`;

  const updateNavInfo = () => {
    if (ref) {
      const rect = ref.getBoundingClientRect();
      rawNavInfo.set({ rect });
    }
  };

  const lg = createBreakpointMediaQueryStore('lg');
</script>

<svelte:window
  on:resize={() => {
    updateNavInfo();
  }}
  on:scroll={() => {
    updateNavInfo();
  }}
/>

<div
  class={cn('sticky z-50 h-12 lg:h-14', isBannerSticky ? 'top-12 lg:top-14' : 'top-0')}
  bind:this={ref}
>
  <!-- [HEY-620] - div used only for the YOTPO loyalty program -->
  <div
    id="swell-customer-identification"
    data-authenticated={$user ? 'true' : 'false'}
    data-email={$user ? $user.email : ''}
    data-id={$user ? trimId($user.id) : ''}
    data-tags={$user ? $user.tags : ''}
    style="display: none;"
  />

  <header
    class={cn('w-full border-b bg-background px-4 transition-colors lg:px-8 border-primary/4')}
  >
    {#if $lg}
      <NavigationHeaderDesktop
        homeUrl={$buildPath({
          path: '/'
        })}
        wishlistUrl={$buildPath({ path: '/wishlist' })}
        accountUrl={$buildPath({ path: '/account/profile' })}
        {showProgress}
      />
    {:else}
      <NavigationHeaderMobile
        homeUrl={$buildPath({
          path: '/'
        })}
        wishlistUrl={$buildPath({ path: '/wishlist' })}
      />
    {/if}
  </header>
</div>

<!-- Desktop menu -->
{#if $layoutPanel === 'menu'}
  <nav
    use:bodyLock
    use:escapeKey={{
      id: 'desktop-navigation-menu',
      callback: () => {
        layoutPanel.close();
      }
    }}
    transition:customSlide={{ duration: $reducedMotion ? 0 : 250, from: -10, to: 0 }}
    on:mouseenter={() => {
      layoutPanel.open('menu');
    }}
    on:mouseleave={() => {
      layoutPanel.close();
    }}
    id={a11yIds.contentDesktop}
    aria-labelledby={a11yIds.triggerDesktop}
    class={cn(
      'fixed z-40 hidden w-full border-b border-primary/4 bg-background px-8 lg:block',
      'after:pointer-events-none after:absolute after:left-0 after:right-0 after:top-full after:h-[100vh] after:bg-primary/40 top-[--dynamic-navbar-height]'
    )}
    style="--dynamic-navbar-height: {topnavBarHeight}"
  >
    {#if isBasicNavbar && !!headerContent?.find((item) => item.show_on_desktop)}
      <NavigationMenuBasicDesktop {configuration} />
    {:else}
      <NavigationMenuDesktop {configuration} />
    {/if}
  </nav>
{/if}

<!-- Mobile menu -->
{#if $layoutPanel === 'menu'}
  <nav
    use:bodyLock
    use:escapeKey={{ id: 'mobile-navigation-menu', callback: () => layoutPanel.close() }}
    transition:customSlide={{ duration: $reducedMotion ? 0 : 250, from: -10, to: 0 }}
    id={a11yIds.contentMobile}
    aria-labelledby={a11yIds.triggerMobile}
    class={cn(
      'fixed inset-0 z-[45] w-full bg-background lg:hidden',
      isBannerSticky ? 'top-24' : 'top-[--dynamic-navbar-height]'
    )}
    style="--dynamic-navbar-height: {topnavBarHeight}"
  >
    {#if isBasicNavbar && !!headerContent?.find((item) => item.show_on_mobile)}
      <NavigationMenuBasicMobile {configuration} />
    {:else}
      <NavigationMenuMobile {configuration} />
    {/if}
  </nav>
{/if}

<!-- Search Menu -->
{#if $layoutPanel === 'search'}
  <aside
    use:bodyLock
    use:escapeKey={{
      id: 'search-panel',
      callback: () => {
        layoutPanel.close();
      }
    }}
    transition:customSlide={{ duration: $reducedMotion ? 0 : 250, from: -10, to: 0 }}
    id={a11yIds.contentSearch}
    aria-labelledby={a11yIds.triggerSearch}
    class={cn(
      'fixed inset-0 lg:bottom-[unset] z-40 overflow-y-auto lg:overflow-y-visible w-full border-b border-primary/4 bg-background px-2 lg:px-8',
      isBannerSticky ? 'top-[90px] md:top-24 lg:top-24' : 'top-[--dynamic-navbar-height]'
    )}
    style="--dynamic-navbar-height: {topnavBarHeight}"
  >
    <SearchPanel class="container mx-auto" />

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      role="button"
      tabindex="0"
      transition:fade|global={{ duration: 200 }}
      on:click={() => layoutPanel.close()}
      class="absolute hidden lg:block left-0 right-0 top-full h-[100vh] bg-primary/40"
    />
  </aside>
{/if}
